import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { reviewService } from '@/services/reviewService';
import { toReviewError, type ReviewError } from '../utils/errorMap';
import type { ResourceMeta } from '../types/resource';

interface UseResourceMetaReturn {
  meta: ResourceMeta | null;
  loading: boolean;
  error: ReviewError | null;
  refetch: () => void;
}

const META_STALE_MS = 10 * 60 * 1000;

export function useResourceMeta(resource: string | undefined): UseResourceMetaReturn {
  const queryClient = useQueryClient();
  const query = useQuery<ResourceMeta, unknown>({
    queryKey: ['impact-data', 'resource-meta', resource],
    queryFn: () => reviewService.getResourceMeta(resource as string),
    enabled: Boolean(resource),
    staleTime: META_STALE_MS,
  });

  const refetch = useCallback(() => {
    if (!resource) return;
    queryClient.invalidateQueries({
      queryKey: ['impact-data', 'resource-meta', resource],
    });
  }, [queryClient, resource]);

  return {
    meta: query.data ?? null,
    loading: query.isPending && Boolean(resource),
    error: query.error ? toReviewError(query.error) : null,
    refetch,
  };
}
